import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Checkbox, Textarea } from "@/components/ui/form-controls";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/toast";
import { localOnly } from "@/lib/fixture-notice";
import { cn } from "@/lib/utils";
import { useTransferStore } from "@/stores/transfer-store";
import { usePermission } from "@/hooks/use-permission";
import type { ApprovalDetail } from "@/types";

export type EscalateTab = "reject" | "hold" | "escalate";

const TABS: { id: EscalateTab; label: string }[] = [
  { id: "reject", label: "Reject" },
  { id: "hold", label: "Hold for Review" },
  { id: "escalate", label: "Escalate" },
];

const REJECT_REASONS = [
  "Destination not on approved recipient list",
  "Policy violation — velocity limit",
  "Screening failed — high risk address",
  "Insufficient authorization",
  "Other",
];

const ESCALATE_REASONS = [
  "Approval SLA expiring",
  "Requires senior approver",
  "Dispute between approvers",
  "Additional documentation required",
  "Other",
];

export default function ApprovalEscalate({ detail, initialTab }: {
  detail: ApprovalDetail;
  initialTab: EscalateTab;
}) {
  const navigate = useNavigate();
  const rejectTransfer = useTransferStore((s) => s.rejectTransfer);
  const holdTransfer = useTransferStore((s) => s.holdTransfer);
  const escalateTransfer = useTransferStore((s) => s.escalateTransfer);
  const canReject = usePermission("approval:reject");
  const canHold = usePermission("approval:hold");
  const canEscalate = usePermission("approval:escalate");
  const permittedTabs = TABS.filter((t) =>
    t.id === "reject" ? canReject : t.id === "hold" ? canHold : canEscalate
  );
  const [tab, setTab] = React.useState<EscalateTab>(
    permittedTabs.find((t) => t.id === initialTab)?.id ?? permittedTabs[0]?.id ?? "reject"
  );
  const [rejectReason, setRejectReason] = React.useState(REJECT_REASONS[0]);
  const [escalateReason, setEscalateReason] = React.useState(ESCALATE_REASONS[0]);
  const [holdReason, setHoldReason] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [evidence, setEvidence] = React.useState(true);

  const canSubmit = tab !== "hold" || holdReason.trim().length > 0;

  const submit = () => {
    if (tab === "reject") {
      rejectTransfer(detail.transferId, rejectReason, notes, evidence);
    } else if (tab === "hold") {
      holdTransfer(detail.transferId, holdReason);
    } else {
      escalateTransfer(detail.transferId, escalateReason, notes);
    }
    toast({
      variant: "success",
      title: `Transfer ${detail.transferId} ${tab === "reject" ? "rejected" : tab === "hold" ? "held for review" : "escalated"}`,
      description: localOnly(`${detail.approvalId} updated in the admin audit trail.`),
    });
    navigate("/approvals");
  };

  return (
    <>
      <Breadcrumb
        items={[{ label: "Approvals", href: "/approvals" }, { label: "Reject / Hold / Escalate" }]}
      />
      <div className="max-w-[680px]">
        <h1 className="text-xl font-bold text-ink">Reject / Hold / Escalate</h1>
        <p className="mt-1 text-[12.5px] text-muted">
          {detail.approvalId} — Transfer {detail.transferId}
        </p>

        {permittedTabs.length === 0 ? (
          <div className="mt-5 rounded-lg border border-line bg-bg px-4 py-6 text-sm text-muted">
            You do not have permission to reject, hold, or escalate this approval.
          </div>
        ) : (
          <div aria-label="Decision type" className="mt-5 flex flex-wrap gap-2" role="group">
            {permittedTabs.map((t) => (
              <button
                key={t.id}
                type="button"
                aria-pressed={tab === t.id}
                onClick={() => setTab(t.id)}
                className={cn(
                  "rounded-md border px-5 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-soft",
                  tab === t.id
                    ? "border-danger bg-danger text-white"
                    : "border-line bg-surface text-ink hover:bg-bg"
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="mt-5 rounded-lg border border-danger-border bg-danger-soft p-6">
          <div className="text-[17px] font-bold text-danger">
            {tab === "reject" && `Reject Transfer ${detail.transferId}`}
            {tab === "hold" && `Hold Transfer ${detail.transferId} for Review`}
            {tab === "escalate" && `Escalate Transfer ${detail.transferId}`}
          </div>
          <p className="mt-1 max-w-[552px] text-[12.5px] leading-relaxed text-warning">
            {tab === "reject" &&
              "Rejecting this transfer will terminate it. The requester will be notified. An evidence receipt will be generated."}
            {tab === "hold" &&
              "Holding this transfer will pause it until a decision is made. The requester will be notified. This action is recorded in the audit trail."}
            {tab === "escalate" &&
              "Escalating this transfer will reassign it to a senior approver for review. The requester and the approval route will be notified."}
          </p>

          {tab === "reject" && (
            <>
              <div className="mt-4">
                <label htmlFor="reject-reason" className="mb-1.5 block text-[12px] font-semibold text-ink">
                  Rejection Reason <span className="text-danger">(required)</span>
                </label>
                <Select
                  id="reject-reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full border-danger/50 focus:border-danger"
                >
                  {REJECT_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="mt-3">
                <label htmlFor="reject-notes" className="mb-1.5 block text-[12px] font-semibold text-ink">
                  Additional Notes
                </label>
                <Textarea
                  id="reject-notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="border-danger/50 focus:border-danger"
                  placeholder="Optional context for the audit record"
                />
              </div>
            </>
          )}

          {tab === "hold" && (
            <div className="mt-4">
              <label htmlFor="hold-notes" className="mb-1.5 block text-[12px] font-semibold text-ink">
                Hold Reason <span className="text-danger">(required)</span>
              </label>
              <Textarea
                id="hold-notes"
                rows={3}
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                className="border-danger/50 focus:border-danger"
                placeholder="Describe why this transfer is being held for review"
              />
            </div>
          )}

          {tab === "escalate" && (
            <>
              <div className="mt-4">
                <label htmlFor="escalate-reason" className="mb-1.5 block text-[12px] font-semibold text-ink">
                  Escalation Reason <span className="text-danger">(required)</span>
                </label>
                <Select
                  id="escalate-reason"
                  value={escalateReason}
                  onChange={(e) => setEscalateReason(e.target.value)}
                  className="w-full border-danger/50 focus:border-danger"
                >
                  {ESCALATE_REASONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="mt-3">
                <label htmlFor="escalate-notes" className="mb-1.5 block text-[12px] font-semibold text-ink">
                  Additional Context
                </label>
                <Textarea
                  id="escalate-notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="border-danger/50 focus:border-danger"
                  placeholder="Optional supporting notes for the senior approver"
                />
              </div>
            </>
          )}

          <div className="mt-3 flex items-center gap-2">
            <Checkbox checked={evidence} onCheckedChange={setEvidence} ariaLabel="Generate evidence packet for audit" />
            <span className="text-[13px] text-ink">Generate evidence packet for audit</span>
          </div>

          <div className="mt-4 flex items-center gap-2.5">
            <Button
              variant="destructive"
              onClick={submit}
              disabled={!canSubmit || permittedTabs.length === 0}
            >
              {tab === "reject" && "Confirm Rejection"}
              {tab === "hold" && "Confirm Hold"}
              {tab === "escalate" && "Confirm Escalation"}
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/approvals/${detail.id}`)}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}